import type React from 'react';
import { useAvatarStore } from '@features/avatar-integration';
import {
  Panel,
  Toggle,
  RadioOption,
  SliderField,
  SelectField,
  InfoIcon,
  EyeIcon,
  CornerBottomLeftIcon,
  CornerBottomRightIcon,
  CenterIcon,
  CustomFrameIcon,
} from '@shared/ui';
import type { AvatarAutonomyMode, AvatarPlacementPreset, AvatarVisibility } from '@shared/types';

const AUTONOMY_OPTIONS: { value: AvatarAutonomyMode; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'assisted', label: 'Assisted' },
  { value: 'autonomous', label: 'Autonomous' },
];

const PLACEMENT_OPTIONS: {
  value: AvatarPlacementPreset;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { value: 'bottom-left', label: 'Bottom Left', icon: CornerBottomLeftIcon },
  { value: 'bottom-right', label: 'Bottom Right', icon: CornerBottomRightIcon },
  { value: 'center', label: 'Center', icon: CenterIcon },
  { value: 'custom', label: 'Custom', icon: CustomFrameIcon },
];

const VISIBILITY_OPTIONS: { value: AvatarVisibility; label: string }[] = [
  { value: 'visible', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
];

export function AvatarControlsPanel() {
  const controls = useAvatarStore((state) => state.controls);
  const setControlsOverlayEnabled = useAvatarStore((state) => state.setControlsOverlayEnabled);
  const setAutonomy = useAvatarStore((state) => state.setAutonomy);
  const setPlacement = useAvatarStore((state) => state.setPlacement);
  const setScale = useAvatarStore((state) => state.setScale);
  const setRotation = useAvatarStore((state) => state.setRotation);

  return (
    <Panel
      title="AVATAR CONTROLS (RESERVED FOR UNITY INTEGRATION)"
      titleAccessory={<InfoIcon className="h-4 w-4 text-text-muted" />}
    >
      <div className="grid grid-cols-1 gap-x-10 gap-y-6 lg:grid-cols-3">
        {/* Column 1: Status / Renderer / Overlay */}
        <div className="space-y-4">
          <FieldRow label="Status">
            <ReadOnlyField value="Not Connected" />
          </FieldRow>
          <FieldRow label="Renderer">
            <ReadOnlyField value="Unity" />
          </FieldRow>
          <FieldRow label="Overlay">
            <Toggle
              checked={controls.overlayEnabled}
              onChange={setControlsOverlayEnabled}
              label={controls.overlayEnabled ? 'Enabled' : 'Disabled'}
            />
          </FieldRow>
        </div>

        {/* Column 2: Autonomy + Placement */}
        <div className="space-y-5">
          <div>
            <div className="mb-3 text-sm text-text-secondary">Avatar Autonomy</div>
            <div className="flex flex-wrap gap-5">
              {AUTONOMY_OPTIONS.map((option) => (
                <RadioOption
                  key={option.value}
                  selected={controls.autonomy === option.value}
                  onSelect={() => setAutonomy(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="mb-3 text-sm text-text-secondary">Placement</div>
            <div className="flex flex-wrap gap-5">
              {PLACEMENT_OPTIONS.map((option) => (
                <RadioOption
                  key={option.value}
                  selected={controls.placement === option.value}
                  onSelect={() => setPlacement(option.value)}
                  label={option.label}
                  icon={<option.icon className="h-4 w-4 text-text-secondary" />}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Column 3: Scale / Rotation / Visibility */}
        <div className="space-y-4">
          <SliderField
            label="Scale"
            value={controls.scalePercent}
            min={25}
            max={200}
            unit="%"
            onChange={setScale}
          />
          <SliderField
            label="Rotation"
            value={controls.rotationDegrees}
            min={-180}
            max={180}
            unit="\u00B0"
            onChange={setRotation}
          />
          <FieldRow label="Visibility">
            <SelectField
              icon={<EyeIcon className="h-4 w-4" />}
              value={controls.visibility}
              onChange={() => {
                /* Reserved for Unity integration */
              }}
              options={VISIBILITY_OPTIONS}
              className="py-1.5"
            />
          </FieldRow>
        </div>
      </div>

      <p className="mt-6 text-xs text-text-muted">(Reserved for Unity Renderer Integration)</p>
    </Panel>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-text-secondary">{label}</span>
      {children}
    </div>
  );
}

function ReadOnlyField({ value }: { value: string }) {
  return (
    <span className="rounded-md border border-surface-border bg-surface-2 px-3 py-1.5 text-sm text-text-primary">
      {value}
    </span>
  );
}
